import {CorePlugin, PluginType} from "./PluginType";

/**
 * Registry for managing all platform plugins
 */
export class PluginRegistry {

    private plugins: Map<string, CorePlugin> = new Map();
    private pluginsByType: Map<PluginType, CorePlugin[]> = new Map();

    /**
     * Register a plugin with the platform
     */
    public registerPlugin(plugin: CorePlugin): void {
        // Prevent duplicate plugins
        if (this.plugins.has(plugin.id)) {
            console.warn(`Plugin with ID ${plugin.id} is already registered. Skipping.`);
            return;
        }

        // Store in main registry
        this.plugins.set(plugin.id, plugin);

        // Store by type
        const byType = this.pluginsByType.get(plugin.type) || [];
        byType.push(plugin);
        this.pluginsByType.set(plugin.type, byType);
    }

    /**
     * Unregister a plugin by ID
     */
    public unregisterPlugin(pluginId: string): boolean {
        const plugin = this.plugins.get(pluginId);

        if (!plugin) {
            return false;
        }

        // Remove from main registry
        this.plugins.delete(pluginId);

        // Remove from type registry
        const byType = this.pluginsByType.get(plugin.type) || [];
        this.pluginsByType.set(
            plugin.type,
            byType.filter(p => p.id !== pluginId)
        );
        return true;
    }

    /**
     * Get a single plugin by ID
     */
    public getPlugin<T extends CorePlugin>(pluginId: string): T | undefined {
        return this.plugins.get(pluginId) as T | undefined;
    }

    /**
     * Get all plugins of a specific type
     */
    public getPluginsByType<T extends CorePlugin>(type: PluginType): T[] {
        return (this.pluginsByType.get(type) || []) as T[];
    }

    /**
     * Get all plugins of a specific type filtered by permissions
     * This allows permission filtering to happen at the registry level rather than in components
     */
    public getPluginsByTypeWithPermissions<T extends CorePlugin>(
        type: PluginType,
        hasPermission: (permissions: string[]) => boolean
    ): T[] {
        const plugins = this.getPluginsByType<T>(type);
        return plugins.filter(plugin =>
            hasPermission(plugin.requiredPermissions ?? [])
        );
    }

    /**
     * Get all registered plugins
     */
    public getAllPlugins(): CorePlugin[] {
        return Array.from(this.plugins.values());
    }

    /**
     * Get all registered plugins filtered by permissions
     */
    public getAllPluginsWithPermissions(
        hasPermission: (permissions: string[]) => boolean
    ): CorePlugin[] {
        return this.getAllPlugins().filter(plugin =>
            hasPermission(plugin.requiredPermissions ?? [])
        );
    }

    /**
     * Check if a plugin is registered
     */
    public hasPlugin(pluginId: string): boolean {
        return this.plugins.has(pluginId);
    }
}

// Create a singleton instance
export const pluginRegistry = new PluginRegistry();